import {
  type ExtendedMedicationRequest,
  THREAD_SUPERSESSION_REASON_MARKER,
} from '@seen/core/models/medicationRequest'
import { format as dateFnsFormat, isSameDay, parseISO } from 'date-fns'

// Order/document/renew events are placed and dated by the clinical start date
// (effectiveDate = startDate ?? authoredOn), NOT the day the record was keyed
// in. Elation stamps authoredOn/signedDate from created_date, so a backdated
// documented med (e.g. a 100 mg download signed during reconciliation but
// filled months earlier) would otherwise sort above — and read as newer than —
// the actively-filled order it predates.
const ORDER_EVENT_TYPES = new Set(['initial', 'renewed', 'dose-change'])

// Recency key for placing/dating an order-type activity event. Prefers the
// clinical effectiveDate, then the sign date, then lastUpdated.
export function orderEventTimestamp(
  request: ExtendedMedicationRequest
): string | undefined {
  return request.effectiveDate ?? request.signedDate ?? request.lastUpdated
}

// Picks the timestamp a stopped/cancelled/completed event is dated by.
//
// Normally the recorded stopDate wins. The exception is a same-calendar-day
// stop: stopDate is date-only, so it parses to midnight and would sort above the
// order it stopped. There we fall back to statusChangedDate (when the status was
// actually written).
//
// A thread supersession is exempt from that fallback. Those stops end a dose
// where its successor begins, so a dose replaced the same day it was ordered
// legitimately carries stopDate == its own order date — a real clinical date, not
// the midnight ambiguity the fallback exists for. Falling back would date the
// event to when the supersession was written, which for the stranded-active
// backfill is its run date, months after the therapy actually ended.
export function stopEventTimestamp(
  request: Pick<
    ExtendedMedicationRequest,
    'stopDate' | 'authoredOn' | 'lastUpdated' | 'statusReason'
  >,
  statusChangedDate: string | undefined
): string | undefined {
  const recordedStopDate = request.stopDate
  const isDateOnly = !!recordedStopDate && !recordedStopDate.includes('T')
  const orderDate = request.authoredOn ?? request.lastUpdated
  const isThreadSupersession =
    request.statusReason?.includes(THREAD_SUPERSESSION_REASON_MARKER) ?? false

  let useFallbackTimestamp = false
  if (isDateOnly && recordedStopDate && orderDate && !isThreadSupersession) {
    try {
      useFallbackTimestamp = isSameDay(
        parseISO(recordedStopDate),
        parseISO(orderDate)
      )
    } catch {
      useFallbackTimestamp = false
    }
  }

  return useFallbackTimestamp
    ? statusChangedDate
    : (recordedStopDate ?? statusChangedDate)
}

// When an order/document event is dated by its clinical start date but was
// keyed in on a different day, return a hover detail exposing the authoredOn
// date. Returns undefined when the two coincide (normal eRx) or the event is
// not an order/document, so the tooltip stays clean.
export function authoredOnTooltipDetail(
  eventType: string | undefined,
  request: Pick<
    ExtendedMedicationRequest,
    'authoredOn' | 'startDate' | 'isDocumented'
  >
): string | undefined {
  if (!eventType || !ORDER_EVENT_TYPES.has(eventType)) {
    return undefined
  }
  const authoredOn = request.authoredOn
  const startDate = request.startDate
  if (!authoredOn || !startDate) {
    return undefined
  }
  // Compare calendar days; authoredOn is a datetime and startDate is date-only.
  if (authoredOn.slice(0, 10) === startDate.slice(0, 10)) {
    return undefined
  }
  const verb = request.isDocumented ? 'Documented' : 'Authored'
  return `${verb} ${dateFnsFormat(parseISO(authoredOn), 'MM/dd/yy')}`
}
